import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";

const features = [
  {
    icon: "ri-flashlight-line",
    title: "快速掌握动态",
    desc: "第一时间汇总院线新片、热播剧集与行业资讯，让你不错过任何值得关注的影视动态。",
  },
  {
    icon: "ri-quill-pen-line",
    title: "深度影评分享",
    desc: "汇聚影迷与作者的观影笔记与深度解析，从叙事、影像到主题，多角度读懂一部作品。",
  },
  {
    icon: "ri-compass-3-line",
    title: "轻松发现好片",
    desc: "分类筛选、标签导航与相关推荐相结合，帮助你更高效地找到符合口味的下一个故事。",
  },
];

const values = [
  { num: "01", title: "真实", desc: "不做标题党，用诚恳的内容赢得影迷的信任。" },
  { num: "02", title: "专业", desc: "关注影像表达与创作细节，提供有观点的内容。" },
  { num: "03", title: "温度", desc: "影评不只是打分，更是人与人之间情感的共鸣。" },
  { num: "04", title: "开放", desc: "欢迎每一种声音，让讨论在尊重中生长。" },
];

export default function About() {
  useSeo({
    title: "关于我们 - 亚洲天堂",
    description:
      "亚洲天堂是面向华语影视爱好者的资讯与影评分享平台，致力于提供及时、专业、有温度的影视内容，帮助影迷快速了解影视动态并发现好作品。",
    keywords: "关于亚洲天堂,影视资讯平台,影评分享,平台介绍",
  });

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <section className="relative h-[300px] w-full overflow-hidden bg-primary-950 md:h-[400px]">
        <img
          src="https://readdy.ai/api/search-image?query=Artistic%20cinematic%20image%20of%20a%20rows%20of%20cinema%20seats%20facing%20a%20glowing%20orange%20screen%20in%20a%20dark%20deep%20blue%20theater%2C%20contemplative%20mood%2C%20high%20detail%20editorial%20photography%20with%20soft%20lighting&width=1600&height=900&seq=ya-about-hero-01&orientation=landscape"
          alt="亚洲天堂影视平台"
          title="亚洲天堂 - 关于我们"
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/70 to-primary-950/40"></div>
        <div className="absolute inset-0 mx-auto flex w-full max-w-site flex-col justify-center px-4 md:px-6">
          <div className="mb-4 hidden md:block">
            <span className="text-sm text-primary-200">首页 / 关于我们</span>
          </div>
          <h1 className="font-heading text-3xl font-bold text-background-50 md:text-4xl">关于亚洲天堂</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-primary-100 md:text-base">
            我们是一个专注华语影视的资讯与影评平台，愿你在光影之间，找到属于自己的那份感动。
          </p>
        </div>
      </section>

      <div className="mx-auto w-full max-w-site px-4 py-6 md:px-6">
        <div className="hidden md:block">
          <Breadcrumb items={[{ label: "关于我们" }]} />
        </div>
      </div>

      {/* 定位 */}
      <section className="mx-auto w-full max-w-site px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600">Our Position</span>
            <h2 className="font-heading text-2xl font-bold text-foreground-900 md:text-3xl">我们的定位</h2>
            <p className="mt-4 text-base leading-loose text-foreground-700">
              亚洲天堂诞生于一群影迷的朴素愿望：让优质的影视内容更容易被发现。我们既是资讯的整理者，
              也是影评的分享者，希望通过专业而有温度的笔触，陪伴每一位观众走过一段段光影旅程。
            </p>
            <p className="mt-4 text-base leading-loose text-foreground-700">
              在这里，你可以快速了解电影与电视剧的最新动态，也可以沉浸在影评作者的观点与感受之中。
              我们希望平台既高效，又不失人文的温度。
            </p>
          </div>
          <div className="overflow-hidden rounded-lg bg-background-200">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20cinematic%20image%20of%20a%20stack%20of%20film%20reels%20and%20a%20notebook%20on%20a%20wooden%20desk%20lit%20by%20soft%20orange%20lamp%20against%20deep%20blue%20evening%20window%2C%20cozy%20editorial%20still%20life%2C%20high%20detail&width=1200&height=900&seq=ya-about-visual-01&orientation=landscape"
              alt="影视内容创作"
              title="亚洲天堂 - 平台特色"
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* 平台特色 */}
      <section className="mt-14 bg-background-100">
        <div className="mx-auto w-full max-w-site px-4 py-12 md:px-6 md:py-16">
          <div className="mb-8">
            <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600">What We Offer</span>
            <h2 className="font-heading text-2xl font-bold text-foreground-900">平台特色</h2>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {features.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-2xl text-primary-700">
                  <i className={item.icon} aria-hidden="true"></i>
                </span>
                <h3 className="mb-2 font-heading text-lg font-bold text-foreground-900">{item.title}</h3>
                <p className="text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 愿景与价值 */}
      <section className="mx-auto w-full max-w-site px-4 py-12 md:px-6 md:py-16">
        <div className="mb-8">
          <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600">Our Vision</span>
          <h2 className="font-heading text-2xl font-bold text-foreground-900">发展愿景与价值主张</h2>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {values.map((item) => (
            <div key={item.num} className="rounded-lg border border-background-200 bg-background-50 p-6">
              <span className="font-heading text-3xl font-bold text-accent-500">{item.num}</span>
              <h3 className="mt-3 mb-2 font-heading text-lg font-bold text-foreground-900">{item.title}</h3>
              <p className="text-sm leading-relaxed text-foreground-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto w-full max-w-site px-4 pb-16 md:px-6">
        <div className="flex flex-col items-start justify-between gap-6 rounded-lg bg-primary-900 p-8 md:flex-row md:items-center md:p-10">
          <div>
            <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">想和我们聊聊？</h2>
            <p className="mt-2 text-sm text-primary-200">无论是内容建议、合作交流还是影迷投稿，我们都欢迎你的声音。</p>
          </div>
          <Link
            to="/contact"
            className="rounded-md bg-accent-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 whitespace-nowrap"
          >
            联系我们
          </Link>
        </div>
      </section>
    </div>
  );
}